import { useQuery } from "@tanstack/react-query";
import { RotateCcw, SlidersHorizontal, X } from "lucide-react";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PRESETS, FILTRE_LABELS, filtresActifs } from "@/lib/analyse";
import { cn } from "@/lib/utils";

const SELECT = "h-7 bg-input border border-border rounded-sm text-[11px] px-1.5 min-w-0 max-w-[180px]";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-0.5 min-w-0">
    <span className="eyebrow">{label}</span>
    {children}
  </label>
);

export const FiltresUniversels = ({ filtres, update, reset, onglet }) => {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "superadmin";
  const lignes = useQuery({ queryKey: ["lignes"], queryFn: () => get("/lignes") });
  const produits = useQuery({ queryKey: ["produits"], queryFn: () => get("/produits") });
  const quarts = useQuery({ queryKey: ["quarts"], queryFn: () => get("/quarts") });
  const familles = useQuery({ queryKey: ["familles"], queryFn: () => get("/analyses/familles") });
  const ref = useQuery({ queryKey: ["analyse-referentiels"], queryFn: () => get("/analyse/referentiels") });
  const users = useQuery({ queryKey: ["users-filtre"], queryFn: () => get("/users"), enabled: isAdmin, retry: false });
  const ofs = useQuery({
    queryKey: ["of-filtre", filtres.du, filtres.au],
    queryFn: () => get("/of/archives", { du: filtres.du, au: filtres.au, page_size: 200 }),
    keepPreviousData: true, staleTime: 60000,
  });

  const items = ofs.data?.items || [];
  const equipes = [...new Set(items.map((o) => o.equipe).filter(Boolean))].sort();
  const superviseurs = [...new Set(items.map((o) => o.superviseur).filter(Boolean))].sort();
  const operateurs = (users.data || []).filter((u) => u.role === "operateur");
  const actifs = filtresActifs(filtres);
  const r = ref.data || {};

  return (
    <div data-testid="analyse-filtres" className="panel px-3 py-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          <SlidersHorizontal className="h-3.5 w-3.5 text-primary" />Filtres universels
        </span>
        <div className="flex items-center gap-1 flex-wrap">
          {PRESETS.map((p) => (
            <button key={p.key} data-testid={`preset-${p.key}`} onClick={() => update({ preset: p.key, ...p.range() })}
              className={cn("h-6 px-2 text-[11px] font-mono border rounded-sm transition-colors",
                filtres.preset === p.key ? "bg-accent border-border text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted")}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 ml-auto text-[11px]">
          <input data-testid="filtre-du" type="date" value={filtres.du} className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]"
            onChange={(e) => e.target.value && update({ du: e.target.value, preset: "libre" })} />
          <span className="text-muted-foreground">→</span>
          <input data-testid="filtre-au" type="date" value={filtres.au} className="h-7 bg-input border border-border rounded-sm px-2 font-mono text-[11px]"
            onChange={(e) => e.target.value && update({ au: e.target.value, preset: "libre" })} />
          <button data-testid="filtres-reset" onClick={reset} title="Réinitialiser les filtres"
            className="h-7 px-2 ml-1 border border-border rounded-sm flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-muted">
            <RotateCcw className="h-3 w-3" />{actifs.length > 0 && <span className="font-mono">{actifs.length}</span>}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-2">
        <Field label="Ligne / machine">
          <select data-testid="filtre-ligne" className={SELECT} value={filtres.ligne_id} onChange={(e) => update({ ligne_id: e.target.value })}>
            <option value="">Toutes les lignes</option>
            {(lignes.data || []).map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
          </select>
        </Field>
        <Field label="Produit">
          <select data-testid="filtre-produit" className={SELECT} value={filtres.produit_id} onChange={(e) => update({ produit_id: e.target.value })}>
            <option value="">Tous les produits</option>
            {(produits.data || []).map((p) => <option key={p.id} value={p.id}>{p.code}</option>)}
          </select>
        </Field>
        <Field label="Famille">
          <select data-testid="filtre-famille" className={SELECT} value={filtres.famille} onChange={(e) => update({ famille: e.target.value })}>
            <option value="">Toutes familles</option>
            {(familles.data || []).map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Quart">
          <select data-testid="filtre-quart" className={SELECT} value={filtres.quart_id} onChange={(e) => update({ quart_id: e.target.value })}>
            <option value="">Tous les quarts</option>
            {(quarts.data || []).map((q) => <option key={q.id} value={q.id}>{q.nom}</option>)}
          </select>
        </Field>
        <Field label="Équipe">
          <select data-testid="filtre-equipe" className={SELECT} value={filtres.equipe} onChange={(e) => update({ equipe: e.target.value })}>
            <option value="">Toutes équipes</option>
            {equipes.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </Field>
        <Field label="Superviseur">
          <select data-testid="filtre-superviseur" className={SELECT} value={filtres.superviseur} onChange={(e) => update({ superviseur: e.target.value })}>
            <option value="">Tous superviseurs</option>
            {superviseurs.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        {isAdmin && (
          <Field label="Opérateur">
            <select data-testid="filtre-operateur" className={SELECT} value={filtres.operateur_id} onChange={(e) => update({ operateur_id: e.target.value })}>
              <option value="">Tous opérateurs</option>
              {operateurs.map((o) => <option key={o.id} value={o.id}>{o.nom}</option>)}
            </select>
          </Field>
        )}
        <Field label="Ordre de fabrication">
          <select data-testid="filtre-of" className={SELECT} value={filtres.of_id} onChange={(e) => update({ of_id: e.target.value })}>
            <option value="">Tous les OF ({ofs.data?.total ?? 0})</option>
            {items.map((o) => <option key={o.id} value={o.id}>{o.numero}</option>)}
          </select>
        </Field>
        <Field label="Statut OF">
          <select data-testid="filtre-statut-of" className={SELECT} value={filtres.statut_of} onChange={(e) => update({ statut_of: e.target.value })}>
            <option value="">Tous les statuts</option>
            {(r.statuts_of || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Unité">
          <select data-testid="filtre-unite" className={SELECT} value={filtres.unite} onChange={(e) => update({ unite: e.target.value })}>
            <option value="">Toutes unités</option>
            {(r.unites || []).map((u) => <option key={u.abreviation} value={u.abreviation}>{u.abreviation} — {u.nom}</option>)}
          </select>
        </Field>
        {(onglet === "arrets" || onglet === "vue" || onglet === "trs") && (<>
          <Field label="Catégorie d'arrêt">
            <select data-testid="filtre-categorie-arret" className={SELECT} value={filtres.categorie_arret_id} onChange={(e) => update({ categorie_arret_id: e.target.value })}>
              <option value="">Toutes catégories</option>
              {(r.categories_arret || []).map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </Field>
          <Field label="Type d'arrêt">
            <select data-testid="filtre-type-arret" className={SELECT} value={filtres.type_arret} onChange={(e) => update({ type_arret: e.target.value })}>
              <option value="">Planifiés + non planifiés</option>
              <option value="non_planifie">Non planifiés</option>
              <option value="planifie">Planifiés</option>
            </select>
          </Field>
        </>)}
        {(onglet === "rejets" || onglet === "vue") && (
          <Field label="Motif de rejet">
            <select data-testid="filtre-motif-rejet" className={SELECT} value={filtres.motif_rejet_id} onChange={(e) => update({ motif_rejet_id: e.target.value })}>
              <option value="">Tous motifs</option>
              {(r.motifs_rejet || []).map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
            </select>
          </Field>
        )}
      </div>

      {actifs.length > 0 && (
        <div data-testid="filtres-chips" className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-border/50">
          {actifs.map((k) => (
            <button key={k} data-testid={`chip-${k}`} onClick={() => update({ [k]: "" })}
              className="h-6 px-2 flex items-center gap-1 text-[11px] font-mono border border-primary/50 bg-accent/30 rounded-sm hover:bg-accent">
              {FILTRE_LABELS[k]}<X className="h-3 w-3" />
            </button>
          ))}
          <button data-testid="effacer-filtres" onClick={reset} className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground">
            Effacer tous les filtres
          </button>
        </div>
      )}
    </div>
  );
};
